import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from 'components/common/Link';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import TextField from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import DatepickerField from 'components/fields/DatepickerField';
import FormError from 'components/common/FormError';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';
import patientDobToAge from 'utils/patientDobToAge';

import pxToRem from 'styles/pxToRem';

import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';

import {
  createPatient,
  updatePatient,
  requestSearchPatients,
} from 'redux/modules/patients';

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {
      ...formValues['Patient Status'] === 'true'
        && { 'Patient Status': true },
      ...formValues['Patient Status'] === 'false'
        && { 'Patient Status': false },
    });

    if (normalizedValues['Social Security Number'] !== undefined
      && normalizedValues['Social Security Number'] === '') {
      normalizedValues['Social Security Number'] = null;
    }

    if (normalizedValues['Date of Birth']) {
      normalizedValues['Date of Birth'] =
        moment(normalizedValues['Date of Birth'], dateFormat).format(dateFormat);
    }

    if (normalizedValues['Date of Death'] !== undefined) {
      if (normalizedValues['Date of Death'] === '') {
        normalizedValues['Date of Death'] = null;
      } else {
        normalizedValues['Date of Death'] =
          moment(normalizedValues['Date of Death'], dateFormat).format(dateFormat);
      }
    }

    return normalizedValues;
  };

  if (props['Record Number']) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    if (diffValues['First Name'] || diffValues['Last Name']) {
      return requestSearchPatients({
        filters: [
          {
            name: 'First Name',
            operation: 'eq',
            val: values['First Name'],
            case_sensitivity: false,
          },
          {
            name: 'Last Name',
            operation: 'eq',
            val: values['Last Name'],
            case_sensitivity: false,
          },
          {
            name: 'Date of Birth',
            operation: 'eq',
            val: values['Date of Birth'],
          },
        ],
      }).then((res) => {
        if (res.data.length > 0 && !props.forceUpdate) {
          return Promise.reject({
            error: 'Patient already exists',
            meta: {
              patients: res.data,
            },
          });
        }
        return new Promise((resolve, reject) => {
          dispatch(updatePatient({
            values: {
              'Record Number': props['Record Number'],
              ...normalizeValues(diffValues),
            },
            resolve,
            reject,
          }));
        });
      })
      .then(res => res,
        (err) => {
          if (err.error) {
            throw new SubmissionError({ _error: err.meta && err.meta.patients
              ? err : err.error });
          }
          throw new SubmissionError(err);
        });
    } else {
      return new Promise((resolve, reject) => {
        dispatch(updatePatient({
          values: {
            'Record Number': props['Record Number'],
            ...normalizeValues(diffValues),
          },
          resolve,
          reject,
        }));
      }).then(res => res,
        (err) => {
          if (err.error) {
            throw new SubmissionError({ _error: err.meta && err.meta.patients
              ? err : err.error });
          }
          throw new SubmissionError(err);
        });;
    }
  }
  return new Promise((resolve, reject) => {
    dispatch(createPatient({
      values: {
        ...normalizeValues(values),
        ...props.error && props.error.meta && props.error.meta.patients ? { Force: true } : {},
      },
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.meta && err.meta.patients
          ? err : err.error });
      }
      throw new SubmissionError(err);
    });
}

function onChange(values, dispatch, props, prevValues) {
  if ((!prevValues['Date of Death'] || validate.date(prevValues['Date of Death']))
    && !validate.date(values['Date of Death']) && !validate.date(values['Date of Birth'])
    && moment(values['Date of Death'], dateFormat).isAfter(moment(values['Date of Birth'], dateFormat))) {
    dispatch(props.change('Patient Status', 'false'));
  }
  if (prevValues['Patient Status'] === 'false'
    && values['Patient Status'] === 'true'
    && values['Date of Death']) {
    dispatch(props.change('Date of Death', ''));
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values['Date of Death']
    && !validate.date(values['Date of Death'])
    && values['Date of Birth']
    && !validate.date(values['Date of Birth'])
    && moment(values['Date of Death'], dateFormat).isBefore(moment(values['Date of Birth'], dateFormat))) {
    errors['Date of Death'] = 'Date of Death should be after Date of Birth';
  }

  if (values['Date of Birth']
  && moment(values['Date of Birth'], dateFormat).isAfter(moment().endOf('day'))) {
    errors['Date of Birth'] = 'Date of Birth can not be in future';
  }

  if (values['Date of Death']
  && moment(values['Date of Death'], dateFormat).isAfter(moment().endOf('day'))) {
    errors['Date of Death'] = 'Date of Death can not be in future';
  }

  if (values['Patient Status'] === 'false' && !values['Date of Death']) {
    errors['Date of Death'] = 'Date of Death is required for Inactive Status';
  }

  return errors;
};

const PatientInformationEdit = ({ handleSubmit, change, error, classes }) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error.meta ? (
              <Fragment>
                <div>
                  {error.error}
                </div>
                {error.meta.patients.map(item => (
                  <div key={item.ID}>
                    <Link
                      underline
                      href={`/charts/${item.ID || item['Record Number']}/profile`}
                      target="_blank"
                      color="secondary"
                    >
                      {'- '}
                      {item['Last Name']}
                      {', '}
                      {item['First Name']}
                      {' '}
                      {item['Middle Name'] || ''}   
                      {' | '}
                      {item.ID
                      ? moment(item['Date of Birth'], timeDateFormat).format(dateFormat)
                      : item['Date of Birth']}
                      {`, ${patientDobToAge(item['Date of Birth'])}`}
                      {` | PRN: ${item.ID || item['Record Number']}`}
                    </Link>
                  </div>
                ))}
              </Fragment>
            ) : error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      wrap="wrap"
      spacing={24}
    >
      <Grid item lg={4} xs={12}>
        <Field
          id="First Name"
          name="First Name"
          label="First Name *"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
      <Grid item lg={4} xs={12}>
        <Field
          id="Middle Name"
          name="Middle Name"
          label="Middle Name"
          component={TextField}
          fullWidth
        />
      </Grid>
      <Grid item lg={4} xs={12}>
        <Field
          id="Last Name"
          name="Last Name"
          label="Last Name *"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      wrap="wrap"
      spacing={24}
    >
      <Grid item>
        <FormControl role="group">
          <FormLabel component="legend">Gender</FormLabel>
          <Field
            name="Gender"
            component={RadioGroupField}
          >
            <RadioButtonField value="Male" label="Male" />
            <RadioButtonField value="Female" label="Female" />
            <RadioButtonField value="Other" label="Other" />
          </Field>
        </FormControl>
      </Grid>
      <Grid item lg={3} xs={12}>
        <Field
          id="Date of Birth"
          name="Date of Birth"
          label="Date of Birth *"
          component={DatepickerField}
          validate={[validate.required, validate.date]}
          normalize={normalize.date}
          fullWidth
          minDate="01/01/1900"
          maxDate={moment().format(dateFormat)}
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          endIcon="start"
          errorPlacement="left"
        />
      </Grid>
      <Grid item lg={3} xs={12}>
        <Field
          id="Date of Death"
          name="Date of Death"
          label="Date of Death"
          component={DatepickerField}
          validate={[validate.date]}
          normalize={normalize.date}
          fullWidth
          minDate="01/01/1900"
          maxDate={moment().format(dateFormat)}
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          endIcon="end"
          errorPlacement="left"
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      wrap="nowrap"
      spacing={24}
    >
      <Grid item lg={4} xs={12}>
        <Field
          id="Social Security Number"
          name="Social Security Number"
          label="Social Security Number"
          component={TextField}
          validate={[validate.socialSecurityNumber]}
          normalize={normalize.socialSecurityNumber}
          maxLength="12"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      container
      justify="flex-start"
      wrap="nowrap"
      spacing={24}
    >
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Patient Status *</FormLabel>
          <Field
            name="Patient Status"
            component={RadioGroupField}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Active"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="Inactive"
            />
          </Field>
        </FormControl>
      </Grid>
    </Grid>
  </form>
);

PatientInformationEdit.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.shape({
      error: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

PatientInformationEdit.defaultProps = {
  error: null,
};

export default reduxForm({
  onSubmit,
  onChange,
  validate: validateForm,
  enableReinitialize: true,
  initialValues: {
    Gender: 'Male',
    'Patient Status': 'true',
  },
})(withStyles(styles)(PatientInformationEdit));



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/PatientInformation/PatientInformationEdit.js